import axios from "axios";
import httpRequest from "@/auth/http-client";
import { getInternalBackofficeToken } from "@/auth/internal-token-helper";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IRecoverPassword } from "@/interfaces/auth.interfaces";
import { IPermission } from "@/interfaces/permission.interfaces";
import IUserBackoffice, { IUserBackofficeInput } from "@/interfaces/user-backoffice.interfaces";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

class UserBackofficeService {
  private baseUrl = "v1/user";
  private baseLoginUrl = "/v1/login";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<IUserBackoffice>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const users = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return users;
  }

  async filter(
    filter: IFilter<IUserBackoffice>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IUserBackoffice>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.post(`${this.baseUrl}/filter?${paginationQuery}`, {
      filter,
    });

    return response;
  }

  async getUserById(userId: number): Promise<IUserBackoffice> {
    const internalToken = await getInternalBackofficeToken();
    const apiBaseUrl = import.meta.env.VITE_API_URL || "";

    const response = await axios.get(`${apiBaseUrl}/${this.baseUrl}/${userId}`, {
      headers: {
        Authorization: `Bearer ${internalToken}`,
      },
    });

    return response.data;
  }

  async getUserPermissions(userId: number): Promise<IPermission[]> {
    const internalToken = await getInternalBackofficeToken();
    const apiBaseUrl = import.meta.env.VITE_API_URL || "";

    const response = await axios.get(`${apiBaseUrl}/${this.baseUrl}/${userId}/permissions`, {
      headers: {
        Authorization: `Bearer ${internalToken}`,
      },
    });

    return response.data;
  }

  async getById(userId: number): Promise<IUserBackoffice> {
    const user = await this.getUserById(userId);
    user.permissions = await this.getUserPermissions(userId);

    return user;
  }

  async create(formData: IUserBackofficeInput): Promise<void> {
    return await httpRequest.post(this.baseUrl, formData);
  }

  async update(formData: IUserBackofficeInput, userId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${userId}`, formData);
  }

  async delete(userId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${userId}`);
  }

  async recoverPassword(data: IRecoverPassword): Promise<void> {
    return httpRequest.post(`${this.baseLoginUrl}/recoverpassword`, data);
  }
}

const userBackofficeService = new UserBackofficeService();
export default userBackofficeService;
